import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Box,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import YouTubeIcon from '@mui/icons-material/YouTube';
import BookIcon from '@mui/icons-material/MenuBook';
import NotesIcon from '@mui/icons-material/Notes';
import EmailIcon from '@mui/icons-material/Email';
import { useAuth } from '../../context/AuthContext';
import { getCampaignEpisodeById } from '../../services/campaignEpisodeService';
import { getAllJournalEntries } from '../../services/bennyJournalService';
import { getUsersWithCampaignEmailNotifications, isAdminEmailNotificationSystemEnabled } from '../../services/userService';
import { sendCampaignEpisodeNotifications } from '../../services/emailService';
import EpisodeComments from './EpisodeComments';

const CampaignEpisodeDetailPage = ({ userCharacters = [] }) => {
  const { episodeId } = useParams();
  const navigate = useNavigate();
  const { firestoreUser } = useAuth();
  const [episode, setEpisode] = useState(null);
  const [journalEntries, setJournalEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Email notification state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailError, setEmailError] = useState('');
  const [adminEmailSystemEnabled, setAdminEmailSystemEnabled] = useState(false);
  const [previewRecipients, setPreviewRecipients] = useState([]);

  const isAdmin = firestoreUser && firestoreUser.roles && firestoreUser.roles.admin;
  useEffect(() => {
    const fetchEpisodeAndJournals = async () => {
      if (!episodeId) return;
      
      setLoading(true);
      setError('');
      try {
        const [episodeData, journalsData] = await Promise.all([
          getCampaignEpisodeById(episodeId),
          getAllJournalEntries()
        ]);
        
        if (episodeData) {
          setEpisode(episodeData);
        } else {
          setError('Episode not found.');
        }
        setJournalEntries(journalsData);
        
        // Check if admin email system is enabled for admins
        if (isAdmin && firestoreUser?.uid) {
          const emailSystemEnabled = await isAdminEmailNotificationSystemEnabled(firestoreUser.uid);
          setAdminEmailSystemEnabled(emailSystemEnabled);
        }
      } catch (err) {
        console.error("Error fetching episode details:", err);
        setError('Failed to load episode details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchEpisodeAndJournals();
  }, [episodeId, isAdmin, firestoreUser?.uid]);
  const getJournalTitleById = (journalId) => {
    const entry = journalEntries.find(j => j.id === journalId);
    return entry ? entry.title : 'Unknown Journal Entry';
  };

  const formatRecipientList = (users) => {
    if (!users || users.length === 0) return '';
    
    if (users.length <= 3) {
      // Show all names if 3 or fewer
      return users.map(user => user.name || user.email).join(', ');
    } else if (users.length <= 10) {
      // Show first 2 names and count for 4-10 users
      const firstTwo = users.slice(0, 2).map(user => user.name || user.email).join(', ');
      return `${firstTwo} and ${users.length - 2} others`;
    } else {
      // Show just the count for 10+ users
      return `${users.length} recipients`;
    }
  };
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date unknown';
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    return isNaN(date.getTime()) ? 'Date unknown' : date.toLocaleDateString();
  };
  const handleSendEmailNotification = async () => {
    setEmailError('');
    
    try {
      // Fetch users to show preview in dialog
      const users = await getUsersWithCampaignEmailNotifications();
      setPreviewRecipients(users);
      setEmailDialogOpen(true);
    } catch (err) {
      console.error('Error fetching users for email preview:', err);
      setEmailError('Failed to load email recipients. Please try again.');
    }
  };

  const confirmSendEmails = async () => {
    if (!episode) return;

    try {
      setSendingEmails(true);
      setEmailError('');
      setEmailSuccess('');      // Use already-fetched users from preview
      const users = previewRecipients;
      
      if (users.length === 0) {
        setEmailError('No users have email notifications enabled for campaign episodes.');
        return;
      }      // Send notifications
      await sendCampaignEpisodeNotifications(episode, users, firestoreUser);
      
      const recipientList = formatRecipientList(users);
      setEmailSuccess(`Email notifications sent successfully to ${users.length} user(s): ${recipientList}`);
      setEmailDialogOpen(false);
    } catch (err) {
      console.error('Error sending email notifications:', err);
      setEmailError('Failed to send email notifications. Please try again.');
    } finally {
      setSendingEmails(false);
    }
  };
  const closeEmailDialog = () => {
    setEmailDialogOpen(false);
    setPreviewRecipients([]);
    setEmailError('');
    setEmailSuccess('');
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress sx={{ color: '#d98d2a' }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ '.MuiAlert-message': { color: 'white' }, backgroundColor: '#5c3c10' }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/campaign-episodes')}
          sx={{ mt: 2, color: '#d98d2a', borderColor: '#d98d2a' }}
          variant="outlined"
        >
          Back to Episodes
        </Button>
      </Container>
    );
  }

  if (!episode) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ color: '#F5F5F5', fontFamily: '"Georgia", serif' }}>
          Episode not found.
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/campaign-episodes')}
          sx={{ mt: 2, color: '#d98d2a', borderColor: '#d98d2a' }}
          variant="outlined"
        >
          Back to Episodes
        </Button>
      </Container>
    );
  }
  return (
    <Container maxWidth="lg" sx={{ mt: 4, color: 'white' }}>
      {/* Success/Error Messages */}
      {emailSuccess && (
        <Alert severity="success" sx={{ mb: 2, '.MuiAlert-message': { color: 'white' }, backgroundColor: '#2e7d32' }}>
          {emailSuccess}
        </Alert>
      )}
      {emailError && (
        <Alert severity="error" sx={{ mb: 2, '.MuiAlert-message': { color: 'white' }, backgroundColor: '#5c3c10' }}>
          {emailError}
        </Alert>
      )}

      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/campaign-episodes')}
        sx={{ 
          mb: 3, 
          color: '#d98d2a', 
          borderColor: '#d98d2a',
          fontFamily: '"Georgia", serif'
        }}
        variant="outlined"
      >
        Back to Episodes
      </Button>

      {/* Episode Details */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          backgroundColor: '#3c3c3c', 
          border: '1px solid #555',
          color: '#F5F5F5',
          borderRadius: '8px',
          mb: 3
        }}
      >
        {/* Episode Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h3" component="h1" sx={{ 
            fontFamily: '"Georgia", serif', 
            color: '#d98d2a', 
            mb: 1 
          }}>
            {episode.episodeNumber ? `Episode ${episode.episodeNumber}: ` : ''}{episode.title}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip 
              label={`Logged: ${formatDate(episode.createdAt)}`}
              sx={{ 
                backgroundColor: '#d98d2a', 
                color: '#1c1c1c',
                fontFamily: '"Georgia", serif',
                pointerEvents: 'none'
              }}
            />
            {episode.createdBy?.userName && (
              <Chip 
                label={`By: ${episode.createdBy.userName}`}
                sx={{ 
                  backgroundColor: '#555', 
                  color: '#F5F5F5',
                  fontFamily: '"Georgia", serif',
                  pointerEvents: 'none'
                }}
              />
            )}
          </Box>
        </Box>

        {/* Description */}
        {episode.description && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
              <NotesIcon sx={{ color: '#d98d2a', mr: 1, mt: 0.5 }} />
              <Typography variant="h5" sx={{ color: '#d98d2a', fontFamily: '"Georgia", serif' }}>
                Episode Summary
              </Typography>
            </Box>
            <Typography 
              variant="body1" 
              sx={{ 
                fontFamily: '"Georgia", serif', 
                whiteSpace: 'pre-wrap', 
                color: '#E0E0E0',
                lineHeight: 1.6,
                backgroundColor: '#2a2a2a',
                p: 2,
                borderRadius: 1,
                border: '1px solid #555'
              }}
            >
              {episode.description}
            </Typography>
          </Box>
        )}

        {/* Action Items */}
        {episode.actionItems && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ 
              color: '#d98d2a', 
              fontFamily: '"Georgia", serif',
              mb: 1
            }}>
              Action Items
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                fontFamily: '"Georgia", serif', 
                whiteSpace: 'pre-wrap', 
                color: '#E0E0E0',
                lineHeight: 1.6,
                backgroundColor: '#2a2a2a',
                p: 2,
                borderRadius: 1,
                border: '1px solid #555'
              }}
            >
              {episode.actionItems}
            </Typography>
          </Box>
        )}

        {/* Links Section */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {episode.youtubeVideoUrl && (
            <Button
              variant="contained"
              startIcon={<YouTubeIcon />}
              href={episode.youtubeVideoUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontFamily: '"Georgia", serif',
                backgroundColor: '#FF0000',
                color: 'white',
                '&:hover': { 
                  backgroundColor: '#D40000'
                }
              }}
            >
              Watch on YouTube
            </Button>
          )}          {episode.relatedBennyJournalId && (
            <Button
              variant="contained"
              startIcon={<BookIcon />}
              onClick={() => {
                const journalEntry = journalEntries.find(j => j.id === episode.relatedBennyJournalId);
                if (journalEntry && journalEntry.downloadURL) {
                  navigate(`/view-pdf/${journalEntry.id}`, { 
                    state: { 
                      pdfUrl: journalEntry.downloadURL,
                      journalTitle: journalEntry.title,
                      from: `/campaign-episodes/${episodeId}`
                    } 
                  });
                } else {
                  navigate(`/benny-journal/${episode.relatedBennyJournalId}`, {
                    state: { from: `/campaign-episodes/${episodeId}` }
                  });
                }
              }}
              sx={{
                fontFamily: '"Georgia", serif',
                backgroundColor: '#66bb6a',
                color: 'white',
                '&:hover': { 
                  backgroundColor: '#4caf50'
                }
              }}            >
              Journal: {getJournalTitleById(episode.relatedBennyJournalId)}
            </Button>
          )}          {/* Email Notification Button - Admin Only AND Email System Enabled */}
          {isAdmin && adminEmailSystemEnabled && (
            <Button
              variant="contained"
              startIcon={<EmailIcon />}
              onClick={handleSendEmailNotification}
              sx={{
                fontFamily: '"Georgia", serif',
                backgroundColor: '#2196F3',
                color: 'white',
                '&:hover': { 
                  backgroundColor: '#1976D2'
                }
              }}
            >
              Send Email Notifications
            </Button>
          )}
        </Box>
      </Paper>      {/* Comments Section */}
      <EpisodeComments 
        episodeId={episodeId} 
        userCharacters={userCharacters}
      />

      {/* Email Notification Confirmation Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={closeEmailDialog}
        aria-labelledby="email-dialog-title"
      >
        <DialogTitle id="email-dialog-title">
          Send Email Notifications
        </DialogTitle>        <DialogContent>
          <DialogContentText>
            Are you sure you want to send email notifications about "{episode?.title}" to users who have enabled campaign episode notifications?
          </DialogContentText>
          
          {previewRecipients.length > 0 ? (
            <DialogContentText sx={{ mt: 2, color: '#1976d2' }}>
              <strong>Recipients ({previewRecipients.length}):</strong><br />
              {formatRecipientList(previewRecipients)}
            </DialogContentText>
          ) : (
            <DialogContentText sx={{ mt: 2, color: '#d32f2f' }}>
              <strong>No users have opted in to email notifications.</strong>
            </DialogContentText>
          )}
          
          <DialogContentText sx={{ mt: 2, fontWeight: 'bold' }}>
            This action cannot be undone and will send emails immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEmailDialog} disabled={sendingEmails}>
            Cancel
          </Button>
          <Button 
            onClick={confirmSendEmails} 
            disabled={sendingEmails}
            variant="contained"
            sx={{ backgroundColor: '#2196F3' }}
          >
            {sendingEmails ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {sendingEmails ? 'Sending...' : 'Send Emails'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CampaignEpisodeDetailPage;
